/* eslint-disable @typescript-eslint/no-explicit-any */
import { debounce } from '@mui/material';
import {
 Dispatch,
 MutableRefObject,
 SetStateAction,
 useEffect,
 useMemo,
 useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { usePage } from '../../../contexts/usePageContext';
import { setConfigStorageItem } from '../../../hooks/useConfigStorage';
import { PageNameType } from '../../../redux/clientConfiguration/clientConfigurationSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import reduxSlicesList from '../../../redux/reduxSlicesList';
import { RootState } from '../../../redux/store';
import { FormValues, StructureType } from '../../../types/types';
import { findStructure, getTranslation } from '../../../utils/dataTypes';
import { CustomGridApi, TableModalState } from '../XTable/types';
import { mapFormToPatch } from './utils';

const useEditForm = (props: {
 modalState?: TableModalState;
 setModalState: Dispatch<SetStateAction<TableModalState>>;
 setCreateModalOpen: Dispatch<SetStateAction<boolean>>;
 isOpen: boolean;
 apiRef: MutableRefObject<CustomGridApi>;
 handleClose: () => void;
}) => {
 const { pageName } = usePage();
 const slice = reduxSlicesList[pageName as keyof typeof reduxSlicesList];
 const dispatch = useAppDispatch();
 const { modalState, isOpen, apiRef, handleClose } = props;

 const formMethods = useForm<FormValues>();
 const { handleSubmit, reset, watch, getValues } = formMethods;

 const selector = useAppSelector(
  (state: RootState) => state[pageName as PageNameType],
 );
 const { item, structure, success, loading } = selector;

 const isCreate = modalState?.action === 'create';
 const isDuplicate = modalState?.action === 'duplicate';

 const hasFieldsConfig = useMemo(
  () => ['results'].includes(pageName),
  [pageName],
 );

 const [changedFields, setChangedFields] = useState<Array<string>>();

 const submitDisabled =
  loading?.createItem ||
  loading?.updateItem ||
  (isCreate || isDuplicate ? false : !(changedFields || []).length);

 useEffect(() => {
  const debouncedHandler = debounce((value, { name, type }) => {
   // console.log('debounce handler', value, name, type);
   if (name) {
    setChangedFields((prev) =>
     prev?.includes(name) ? prev : [...(prev || []), name],
    );
   }
  }, 300);

  const subscription = watch(debouncedHandler);

  return () => {
   subscription.unsubscribe();
  };
 }, [watch]);

 useEffect(() => {
  // if (!isCreate && modalState) {
  //  dispatch(slice.getItemStart({ table: pageName, id: modalState.id }));
  // }
  if (modalState?.id === -1) {
   reset({});
  }
 }, [modalState]); // , isOpen

 useEffect(() => {
  if (!isCreate && item) {
   reset(item); // this func sets default values
   setChangedFields([]);
  }
 }, [item]);

 const onSubmit = (data: FormValues) => {
  const submitData = mapFormToPatch(
   isCreate || isDuplicate ? 'create' : 'update',
   data,
   item,
   structure,
   changedFields,
  );
  console.log('submitData', submitData);
  if (isCreate || isDuplicate) {
   dispatch(
    slice.createItemStart({
     table: pageName,
     data: submitData,
    }),
   );
  } else {
   dispatch(
    slice.updateItemStart({
     table: pageName,
     id: modalState?.id || -1,
     data: submitData,
    }),
   );
  }

  // console.log('Form Submitted:', newObj);
  //   reset(); // Reset the form after submission
 };

 useEffect(() => {
  if (success?.createItem || success?.updateItem) {
   apiRef.current.fetchData();
   handleClose();
  }
 }, [success]);

 const fieldPresets = useSelector(
  (state: RootState) => state.clientConfiguration.fieldPresets,
 );

 const [fields, setFields] = useState<string[]>([]);

 const fieldsWithData = useMemo(() => {
  if (!structure || !Array.isArray(fields)) return [];
  return fields?.map((field) => ({
   ...findStructure(structure, field),
   Key: field,
   Translation: getTranslation(structure, field),
  }));
 }, [fields, item, structure]);

 useEffect(() => {
  const tableEditFields =
   typeof fieldPresets === 'object' && fieldPresets !== null && pageName
    ? fieldPresets[pageName]?.edit
    : undefined;

  if (tableEditFields && hasFieldsConfig) {
   setConfigStorageItem({
    page: 'results',
    key: 'favorites_edit',
    value: tableEditFields,
   });
   setFields(tableEditFields);
  } else if (!hasFieldsConfig) {
   setFields(structure?.map((field: StructureType) => field.Name) || []);
  }
 }, [fieldPresets, structure]);

 return {
  handleSubmit,
  onSubmit,
  formMethods,
  isCreate,
  isDuplicate,
  structure,
  loading,
  submitDisabled,
  fieldsWithData,
  hasFieldsConfig,
  item,
 };
};

export default useEditForm;
